import React from 'react';

import { AsaphButton } from '@/components/shared/Button';
import { FormInput } from '@/components/shared/Inputs';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { Environments, useEnvironment } from '@/providers/EnvironmentProvider';
import { FormHelperText } from '@material-ui/core';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import styles from '../LoginPage.module.scss';

type LoginResponse = {
	jwt: string;
	userId: string;
	teamId: string;
	user: {
		email: string;
		id: string;
		displayName: string;
	};
};

const signUpValidationSchema = Yup.object().shape({
	code: Yup.string().required('Please enter your code'),
});

const VerifyForm: React.FC<{
	email: string;
	onChange: (email: string | null) => void;
}> = ({ email, onChange }) => {
	const {
		api: { url },
		environment,
	} = useEnvironment();
	const { authenticate } = useAuth();
	const fireIncorrectCodeEvent = useAnalyticsEvent('Login_Incorrect_Code');
	const fireSuccessLoginEvent = useAnalyticsEvent('Login_Success');

	return (
		<Formik
			key="asaphSignUn"
			initialValues={{
				code: environment === Environments.LOCAL ? '1234' : '',
			}}
			validationSchema={signUpValidationSchema}
			onSubmit={async (values, formikHelpers) => {
				if (values.code.toString().length < 4) {
					formikHelpers.setSubmitting(false);
					return formikHelpers.setFieldError(
						'code',
						'Code is invalid',
					);
				}
				try {
					const res = await axios.post<LoginResponse>(
						`${url}/auth/login`,
						{
							email,
							code: values.code.toString(),
						},
					);

					if (!res.data.jwt) {
						throw new Error('Response did not produce a valid jwt');
					}

					authenticate(res.data.jwt);
					fireSuccessLoginEvent();
				} catch (err) {
					console.error(err);
					fireIncorrectCodeEvent();
					formikHelpers.setErrors({
						code: 'Invalid code',
					});
				}
				formikHelpers.setSubmitting(false);
			}}
		>
			{({
				handleSubmit,
				handleChange,
				values,
				isSubmitting,
				setFieldValue,
				touched,
				errors,
			}) => (
				<form onSubmit={handleSubmit} id="asaphSignIn">
					<h1 className={styles.heading}>Verification</h1>
					<p className={styles.text}>
					One-time code sent to {' '}
						<span>
						{email}
						</span>
					</p>
					<div className="text-center">
						<FormInput
							id="code"
							name="code"
							type="number"
							placeholder="1234"
							onChange={handleChange}
							value={values.code}
							className={styles['code-input']}
						/>
						{/* <ReactCodeInput
							name="code"
							inputMode="numeric"
							onChange={(value) => {
								setFieldValue('code', value);
								if (value.length > 3) {
									handleSubmit();
								}
							}}
							value={values.code}
							fields={4}
							inputStyle={{
								textAlign: 'center',
								MozAppearance: 'textfield',
								borderRadius: '6px',
								border: '1px solid var(--color-2)',
								boxShadow: '0px 0px 10px 0px rgba(0,0,0,.10)',
								margin: '4px',
								width: '36px',
								height: '42px',
								boxSizing: 'border-box',
								fontSize: '20px',
							}}
						/> */}
					</div>
					<div className="vsb-8" />
					<AsaphButton
						label="Continue"
						type="submit"
						color="blue"
						fullWidth
						disabled={isSubmitting}
					/>
					<p className={styles.smallText}>
					Wrong email? {' '}
						<span
							className={styles.link}
							onClick={() => onChange(null)}
						>
							 Use A Different Email
						</span>{' '}
					</p>
					<div className="vsb-20">
						{!!errors.code && touched.code && (
							<FormHelperText
								error={true}
								classes={{
									root: 'authentication-error-text',
								}}
							>
								{errors.code}
							</FormHelperText>
						)}
					</div>
				</form>
			)}
		</Formik>
	);
};

export default VerifyForm;
